import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

import { publicRoutes } from '@app/router/publicRoutes'
import { privateRoutes } from '@app/router/privateRoutes'
import { routes as paths } from '@app/router/routes'
import { hasActiveSession } from '@core/auth/session'
// Layouts come from the active pack — swap packs in `src/app/layouts/index.ts`.
// OnboardingLayout is published by the pack too; mount it here (as its own '/'
// entry) once the onboarding module lands.
import {
  AppLayout,
  AuthLayout,
  PublicContentLayout,
  SignUpLayout,
  WizardLayout,
} from '@app/layouts'
import { authPaths, authRoutes } from '@modules/auth'
import { learnRoutes } from '@modules/learn'
import { legalRoutes } from '@modules/legal'
import { supportPublicRoutes } from '@modules/support'
import { listingRequestWizardRoutes } from '@modules/listingRequest'
import { ownerOnboardingRoutes, sellerOwnerOnboardingRoutes } from '@modules/ownerOnboarding'

const routes: RouteRecordRaw[] = [
  {
    // `/` is a hand-off, not a screen — the seller portal has no public landing
    // page. A live session goes to the dashboard; everyone else to the single
    // UAE PASS sign-in screen. This also covers the "Home" affordances in
    // AppTopNav / ErrorPage / NotFoundPage, which all push `routes.HOME`.
    //
    // It MUST be registered before the layout records below: every one of them
    // is mounted on '/', and on a score tie vue-router keeps the earliest-added
    // matcher — a '/' redirect nested in a layout's children (as it once was)
    // loses to that layout and renders a blank shell instead of redirecting.
    path: paths.HOME,
    name: 'HOME',
    redirect: () => ({ path: hasActiveSession() ? paths.DASHBOARD : authPaths.LOGIN }),
  },
  {
    // Seller guides, Policies & Legal and the contact form — public, shareable,
    // and mounted outside the app shell so a signed-out reader gets the page and
    // nothing else.
    path: '/',
    component: PublicContentLayout,
    children: [...learnRoutes, ...legalRoutes, ...supportPublicRoutes],
  },
  {
    path: '/',
    component: AuthLayout,
    children: [...publicRoutes],
  },
  {
    path: '/',
    component: SignUpLayout,
    children: [...authRoutes],
  },
  {
    // Guided flows: no sidebar or top bar — see WizardLayout. The gate is per
    // child, not per shell: the listing wizard and the seller's owner onboarding
    // carry `requiresAuth`, while the emailed owner-onboarding link is public —
    // that owner has no account, and the link is their only credential.
    path: '/',
    component: WizardLayout,
    children: [
      ...listingRequestWizardRoutes,
      ...sellerOwnerOnboardingRoutes,
      ...ownerOnboardingRoutes,
    ],
  },
  {
    path: '/',
    component: AppLayout,
    children: [...privateRoutes],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach((to) => {
  const isAuthenticated = hasActiveSession()

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  const requiresGuest = to.matched.some((record) => record.meta.requiresGuest)

  if (requiresAuth && !isAuthenticated) {
    return { path: paths.LOGIN }
  }

  if (requiresGuest && isAuthenticated) {
    return { path: paths.DASHBOARD }
  }

  return true
})

export default router
